import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionInputHandle, Budget } from '@bonusly/common-action-input';
import { useAllowanceBoostId } from '@bonusly/common-rewards';
import { useEventListener } from '@bonusly/events';
import { Recipient } from '@bonusly/recipient-entities';
import { useIsBizyEnabledForUser } from '@bonusly/who-am-i';

import { BonusVia, GiveBoxEvent } from '../events';
import { createRecipientContent } from '../utils/content';
import { parseErrorMessage } from '../utils/error';
import { useBonusBoxAnalytics } from './use-bonus-box-analytics';
import { useBudget } from './use-budget';
import { useGiveRecognition } from './use-give-recognition';
import { usePeerToPeerModules } from './use-peer-to-peer-modules';
import { useProxyGiving, UseProxyGivingResult } from './use-proxy-giving';
import { useSharedConfig } from './use-shared-config';
import { useValidator } from './use-validator';

/**
 * Picks the give-box placeholder translation key. When the user can't give
 * points (no-points user type, zero allowance, etc.) we drop the `+ to add
 * points` hint since typing `+` does nothing in that state.
 */
export const getGiveBoxPlaceholderKey = ({ canGivePoints, isBizyEnabled }: { canGivePoints: boolean; isBizyEnabled: boolean }): string => {
	if (isBizyEnabled) {
		return canGivePoints ? 'bonus_input.placeholder_with_bizy_hint' : 'bonus_input.placeholder_with_bizy_hint_no_points';
	}
	return canGivePoints ? 'bonus_input.placeholder' : 'bonus_input.placeholder_no_points';
};

interface UseGiveBoxOptions {
	ignoreInitEvent?: boolean;
	onSuccess?: VoidFunction;
	via?: BonusVia;
	msTeamsMeetingId?: string;
	draftAssistInteractionIds?: string[];
	proxyGiving?: UseProxyGivingResult;
	/** Initial gift-wrap toggle state (e.g. preset from Bizy's "Edit in Givebox"). Defaults on. */
	initialWrapPost?: boolean;
}

export const useGiveBox = (
	{
		ignoreInitEvent = false,
		onSuccess,
		via,
		msTeamsMeetingId,
		draftAssistInteractionIds,
		proxyGiving: externalProxyGiving,
		initialWrapPost,
	}: UseGiveBoxOptions = {} as UseGiveBoxOptions
) => {
	const inputRef = useRef<ActionInputHandle>(null);
	const analytics = useBonusBoxAnalytics();
	const { id: allowanceBoostId } = useAllowanceBoostId();

	// When the caller owns proxy state (e.g. GiveModal lifts it to sync the AmountChip),
	// use that. Otherwise fall back to a local instance (e.g. MS Teams dialog).
	const internalProxyGiving = useProxyGiving();
	const proxyGiving = externalProxyGiving ?? internalProxyGiving;

	// Recipient exclusion must key off the principal (the resolved giver) when
	// giving as a delegate, so the principal is excluded and the delegate stays
	// a valid recipient.
	const handlers = useSharedConfig({ includeGroups: true, principalId: proxyGiving.selectedPrincipal?.id });
	const modules = usePeerToPeerModules();
	const { t } = useTranslation('recognition');
	const isBizyEnabledForUser = useIsBizyEnabledForUser();
	const [wrapPost, setWrapPost] = useState(initialWrapPost ?? true);

	// Listen for an event to give to a specific user
	// On received, populate the input with the user's name and focus the input
	// NOTE: This can be removed once the give box can only be reached via the modal
	const initGiveToUser = useCallback(
		(data: { recipient: Recipient }) => {
			if (ignoreInitEvent) {
				return;
			}

			if (data && data.recipient) {
				inputRef.current?.insert(createRecipientContent(data.recipient));
			}

			inputRef.current?.focus();
		},
		[ignoreInitEvent]
	);

	useEventListener(GiveBoxEvent.Open, initGiveToUser);

	const baseBudget = useBudget();
	const budget = useMemo(() => {
		if (!proxyGiving.selectedPrincipal) return baseBudget;
		return new Budget({
			...baseBudget.data,
			balance: proxyGiving.selectedPrincipal.givingBalance,
			flashPointsBalance: 0,
			delegateName: proxyGiving.selectedPrincipal.name,
		});
	}, [baseBudget, proxyGiving.selectedPrincipal]);
	const validator = useValidator({ balanceOverride: proxyGiving.selectedPrincipal ? budget.data.balance : undefined });
	const { isSubmitting, onSubmit } = useGiveRecognition({
		wrapPost: proxyGiving.selectedPrincipal ? false : wrapPost,
		draftAssistInteractionIds,
		onBehalfOf: proxyGiving.selectedPrincipal?.id,
		onError: (err: unknown) => {
			const message = parseErrorMessage(err);
			inputRef.current?.error(message);
		},
		onSuccess: () => {
			setWrapPost(true);
			proxyGiving.clearSelection();
			onSuccess?.();
			inputRef.current?.reset();
		},
		via,
		msTeamsMeetingId,
	});

	// The `+ to add points` hint only makes sense when the amount module is
	// available (i.e. the user can actually give points). `usePeerToPeerModules`
	// already drops `amount` for user types that can't give and for zero-allowance
	// zero-point-bonus companies, so we key the placeholder off that.
	const placeholderKey = getGiveBoxPlaceholderKey({
		canGivePoints: modules.includes('amount'),
		isBizyEnabled: isBizyEnabledForUser,
	});

	return {
		allowanceBoostId,
		budget,
		context: 'recognition',
		isSubmitting,
		modules,
		placeholder: t(placeholderKey),
		onSubmit,
		onStartDraft: analytics.trackBonusStart,
		ref: inputRef,
		validator,
		wrapPost,
		setWrapPost,
		proxyGiving,
		...handlers,
	};
};
